import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, RotateCcw, Loader2, CalendarClock } from "lucide-react";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import BalanceSheetTable from "@/components/wizard/BalanceSheetTable";
import RatiosDashboard from "@/components/wizard/RatiosDashboard";
import CoverLetterForm from "@/components/wizard/CoverLetterForm";
import QuadraturaBadge from "@/components/wizard/QuadraturaBadge";
import AdvisorPanel from "@/components/wizard/AdvisorPanel";
import CorrectivesPanel from "@/components/wizard/CorrectivesPanel";
import RoadmapPanel from "@/components/wizard/RoadmapPanel";
import PaywallModal from "@/components/wizard/PaywallModal";
import { useLang } from "@/context/LanguageContext";
import { exportFilePaid, getConfig } from "@/lib/api";
import { WIZARD } from "@/constants/testIds/wizard";

const EMPTY_COVER = {
  company_name: "", period_from: "", period_to: "", generated_at: "",
  vat: "", cf: "", cciaa: "", address: "", phone: "", email: "", website: "", logo_b64: "",
};

export default function ResultsStep({ classifiedItems, results, sector = "generico", onRestart }) {
  const { t, lang } = useLang();
  const [exporting, setExporting] = useState(null);
  const [cover, setCover] = useState(EMPTY_COVER);
  const [advisorReport, setAdvisorReport] = useState("");
  const [includeAdvisorInPdf, setIncludeAdvisorInPdf] = useState(true);
  const [correctivesData, setCorrectivesData] = useState(null);
  const [includeCorrectivesInPdf, setIncludeCorrectivesInPdf] = useState(true);
  const [roadmapData, setRoadmapData] = useState(null);
  const [includeRoadmapInPdf, setIncludeRoadmapInPdf] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallKind, setPaywallKind] = useState("pdf");
  const [betaMode, setBetaMode] = useState(false);

  // Check localStorage for a previously-paid session (from /payment/success page)
  const [paidSessionId, setPaidSessionId] = useState(() => {
    try { return localStorage.getItem("riclassificatore.paid_session.v1") || null; } catch { return null; }
  });

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((cfg) => { if (!cancelled) setBetaMode(!!cfg.beta_mode); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const buildPayload = () => {
    const hasCover = Object.values(cover).some((v) => v);
    return {
      cover: hasCover ? cover : null,
      advisorText: includeAdvisorInPdf && advisorReport ? advisorReport : null,
      correctives: includeCorrectivesInPdf && correctivesData ? correctivesData : null,
      roadmap: includeRoadmapInPdf && roadmapData ? roadmapData : null,
    };
  };

  const handleExport = async (kind) => {
    // Re-fetch payment mode right before the export attempt so a runtime toggle
    // is picked up immediately (no client refresh needed).
    let currentBeta = betaMode;
    try {
      const cfg = await getConfig();
      currentBeta = !!cfg.beta_mode;
      if (currentBeta !== betaMode) setBetaMode(currentBeta);
    } catch (_e) {
      // If config fetch fails, fall back to last-known state
    }

    // Beta mode → skip paywall entirely (backend also bypasses gate via PAYMENT_GATE_ENABLED=false)
    if (!currentBeta && !paidSessionId) {
      setPaywallKind(kind);
      setPaywallOpen(true);
      return;
    }
    setExporting(kind);
    try {
      const p = buildPayload();
      await exportFilePaid(kind, paidSessionId || "beta-mode", results.reclassified, results.ratios, lang, p.cover, p.advisorText, p.correctives, p.roadmap);
      toast.success(t("toast_export_ok"));
    } catch (e) {
      if (e?.response?.status === 402) {
        try { localStorage.removeItem("riclassificatore.paid_session.v1"); } catch (_e) { /* noop */ }
        setPaidSessionId(null);
        setPaywallKind(kind);
        setPaywallOpen(true);
      } else {
        toast.error(t("toast_export_err"));
      }
    } finally {
      setExporting(null);
    }
  };

  const { reclassified, ratios } = results;
  const hasPrior = !!ratios.prior_totals || !!ratios.history;
  const annualized = !!ratios.annualized;
  const periodMonths = ratios.period_months || 12;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            {t("results_title")}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <span className="text-sm text-slate-600">
              {classifiedItems.length} {lang === "it" ? "voci riclassificate" : "items reclassified"}
            </span>
            <QuadraturaBadge quadratura={ratios.quadratura} />
            {betaMode && (
              <span
                data-testid="beta-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-400 bg-gradient-to-r from-amber-100 to-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-900"
                title={lang === "it" ? "Report gratuito ad uso promozionale test" : "Free promotional test report"}
              >
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                Beta · Gratis
              </span>
            )}
            {annualized && (
              <span
                data-testid="annualized-badge"
                className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
                title={t("annualized_desc")}
              >
                <CalendarClock className="h-3.5 w-3.5" strokeWidth={2} />
                {t("annualized_badge").replace("{m}", String(periodMonths))}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid={WIZARD.exportExcelBtn}
            onClick={() => handleExport("excel")}
            variant="outline"
            className="rounded-full border-slate-300"
            disabled={exporting !== null}
          >
            {exporting === "excel" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" strokeWidth={1.75} />}
            {t("export_excel")}
          </Button>
          <Button
            data-testid={WIZARD.exportPdfBtn}
            onClick={() => handleExport("pdf")}
            className="rounded-full bg-blue-700 text-white hover:bg-blue-800"
            disabled={exporting !== null}
          >
            {exporting === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" strokeWidth={1.75} />}
            {t("export_pdf")}
          </Button>
          <Button
            data-testid={WIZARD.newAnalysisBtn}
            onClick={onRestart}
            variant="ghost"
            className="rounded-full text-slate-600 hover:bg-slate-100"
          >
            <RotateCcw className="mr-2 h-4 w-4" strokeWidth={1.75} />
            {t("new_analysis")}
          </Button>
        </div>
      </div>

      {annualized && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={2} />
          <div>
            <div className="font-medium">
              {t("annualized_badge").replace("{m}", String(periodMonths))}
            </div>
            <div className="text-xs text-amber-800">{t("annualized_desc")}</div>
          </div>
        </div>
      )}

      <CoverLetterForm cover={cover} onChange={setCover} sector={sector} />

      <div className="mb-6">
        <AdvisorPanel
          reclassified={reclassified}
          ratios={ratios}
          report={advisorReport}
          onReport={setAdvisorReport}
          includeInPdf={includeAdvisorInPdf}
          onIncludeInPdfChange={setIncludeAdvisorInPdf}
        />
      </div>

      <Tabs defaultValue="ratios" data-testid={WIZARD.resultsTabs}>
        <TabsList className="rounded-full bg-white p-1 shadow-sm ring-1 ring-slate-200">
          <TabsTrigger value="ratios" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t("results_indici")}
          </TabsTrigger>
          <TabsTrigger value="sp" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Stato Patrimoniale
          </TabsTrigger>
          <TabsTrigger value="ce" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            Conto Economico
          </TabsTrigger>
          <TabsTrigger value="corr" data-testid="tab-corr" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t("results_correctives")}
          </TabsTrigger>
          <TabsTrigger value="roadmap" data-testid="tab-roadmap" className="rounded-full data-[state=active]:bg-slate-900 data-[state=active]:text-white">
            {t("results_roadmap")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ratios" className="mt-6">
          <RatiosDashboard ratios={ratios} />
        </TabsContent>

        <TabsContent value="sp" className="mt-6">
          <div className={`grid gap-6 ${hasPrior ? "grid-cols-1" : "lg:grid-cols-2"}`}>
            <BalanceSheetTable title={t("results_sp_attivo")} rows={reclassified.sp_attivo} lang={lang} accent="blue" />
            <BalanceSheetTable title={t("results_sp_passivo")} rows={reclassified.sp_passivo} lang={lang} accent="slate" />
          </div>
        </TabsContent>

        <TabsContent value="ce" className="mt-6">
          <BalanceSheetTable title={t("results_ce")} rows={reclassified.conto_economico} lang={lang} accent="blue" />
        </TabsContent>

        <TabsContent value="corr" className="mt-6">
          <CorrectivesPanel
            reclassified={reclassified}
            ratios={ratios}
            sector={sector}
            onDataChange={setCorrectivesData}
            includeInPdf={includeCorrectivesInPdf}
            onIncludeInPdfChange={setIncludeCorrectivesInPdf}
          />
        </TabsContent>

        <TabsContent value="roadmap" className="mt-6">
          <RoadmapPanel
            reclassified={reclassified}
            ratios={ratios}
            sector={sector}
            correctives={correctivesData}
            roadmap={roadmapData}
            onChange={setRoadmapData}
            includeInPdf={includeRoadmapInPdf}
            onIncludeInPdfChange={setIncludeRoadmapInPdf}
          />
        </TabsContent>
      </Tabs>

      {reclassified.other?.items?.length > 0 && (
        <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="mb-3 font-heading text-sm font-medium text-amber-800">
            {t("unclassified")} — {reclassified.other.items.length}
          </div>
          <ul className="grid gap-1 text-xs text-amber-900 sm:grid-cols-2">
            {reclassified.other.items.slice(0, 20).map((it, i) => (
              <li key={`${it.description}-${i}`} className="flex items-center justify-between border-b border-amber-100 py-1">
                <span>{it.description}</span>
                <span className="font-mono">{new Intl.NumberFormat(lang === "it" ? "it-IT" : "en-US", { style: "currency", currency: "EUR" }).format(it.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payment gate */}
      <PaywallModal
        open={paywallOpen}
        onOpenChange={setPaywallOpen}
        kind={paywallKind}
        exportPayload={{
          reclassified: results.reclassified,
          ratios: results.ratios,
          lang,
          ...buildPayload(),
        }}
      />
    </div>
  );
}
